import React, { useState } from "react";
import Menu from "../Menu";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import useApiUrls from "../../useApiUrls";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";

// Helper function to get cookie value
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

const AddAWSIOTCore = () => {
  const navigate = useNavigate();
  const { id, name } = useParams();
  const { getUrlByOperation } = useApiUrls();

  // State to hold form data - Updated to match API requirements
  const [formData, setFormData] = useState({
    catalogname: "", // Keep for display purposes
    remark: "",
    active: true,
    deviceid: "",
    json: "{}",
    serialid: "",
  });

  // State for loading and validation
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.catalogname.trim()) {
      newErrors.catalogname = "Catalogue name is required";
    }

    if (!formData.remark.trim()) {
      newErrors.remark = "Remark/Description is required";
    }

    if (!formData.deviceid.trim()) {
      newErrors.deviceid = "Device ID is required";
    }

    if (!formData.serialid.trim()) {
      newErrors.serialid = "Serial ID is required";
    }

    // Validate JSON format
    try {
      JSON.parse(formData.json);
    } catch (e) {
      newErrors.json = "Please enter valid JSON format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setMessage("Please fix the errors below");
      return;
    }

    setIsLoading(true);
    setMessage("");

    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      setMessage("Authentication required. Please login again.");
      setTimeout(() => navigate("/login"), 2000);
      setIsLoading(false);
      return;
    }

    // Prepare data according to API requirements
    const dataToSend = {
      remark: formData.remark,
      active: formData.active,
      deviceid: formData.deviceid,
      json: formData.json,
      serialid: formData.serialid,
    };

    try {
      const CatalogInsert ='https://api.dqr-display-tms.bonrix.in/api/AWSIOTCoreSettingOperation/SubUser/Insert';
      
      const response = await axios.post(CatalogInsert, dataToSend, {
        headers: {
          "accept": "text/plain",
          "X-Authorization": token,
          "UserId": userid,
          "Content-Type": "application/json-patch+json",
        },
      });

      setMessage("Catalogue added successfully!");
      
      // Reset form
      setFormData({
        catalogname: "",
        remark: "",
        active: true,
        deviceid: "",
        json: "{}",
        serialid: "",
      });

      // Navigate back after 3 seconds
      setTimeout(() => {
        navigate("/AWSIOTCoreList");
      }, 3000);

    } catch (error) {
      console.error("Error adding Catalogue:", error);
      
      if (error.response?.status === 401) {
        setMessage("Session expired. Please login again.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (error.response?.data?.message) {
        setMessage(`Failed to add Catalogue: ${error.response.data.message}`);
      } else {
        setMessage("Failed to add Catalogue. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    // Handle boolean conversion for select dropdown
    let processedValue = value;
    if (name === "active") {
      processedValue = value === "true";
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: processedValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  // Handle JSON formatting
  const handleJsonChange = (e) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      json: value,
    }));

    // Clear JSON error when user types
    if (errors.json) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        json: "",
      }));
    }
  };

  // Format JSON on blur
  const handleJsonBlur = () => {
    try {
      const formatted = JSON.stringify(JSON.parse(formData.json), null, 2);
      setFormData((prevData) => ({
        ...prevData,
        json: formatted,
      }));
    } catch (e) {
      // Keep original value if JSON is invalid
    }
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1>Add AWS IoT Core Setting</h1>
                </div>
                <div className="col-sm-6">
                  <ol className="breadcrumb float-sm-right">
                    <li className="breadcrumb-item">
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => navigate("/AWSIOTCoreList")}
                      >
                        <i className="fas fa-arrow-left mr-1"></i>
                        Back to List
                      </button>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <div className="card card-info">
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-plus mr-2"></i>
                        Add New Catalogue
                      </h3>
                    </div>
                    <div className="card-body">
                      <form id="add-items" onSubmit={handleSubmit}>
                        <div className="row">
                          {/* Catalogue Name */}
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label htmlFor="Cataloguename">
                                Catalogue Name <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${errors.catalogname ? 'is-invalid' : ''}`}
                                id="catalogname"
                                name="catalogname"
                                value={formData.catalogname}
                                onChange={handleChange}
                                placeholder="Enter Catalogue name"
                                disabled={isLoading}
                              />
                              {errors.catalogname && (
                                <div className="invalid-feedback">{errors.catalogname}</div>
                              )}
                            </div>
                          </div>

                          {/* Remark/Description */}
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label htmlFor="remark">
                                Description/Remark <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${errors.remark ? 'is-invalid' : ''}`}
                                id="remark"
                                name="remark"
                                value={formData.remark}
                                onChange={handleChange}
                                placeholder="Enter description"
                                disabled={isLoading}
                              />
                              {errors.remark && (
                                <div className="invalid-feedback">{errors.remark}</div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          {/* Device ID */}
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label htmlFor="deviceid">
                                Device ID <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${errors.deviceid ? 'is-invalid' : ''}`}
                                id="deviceid"
                                name="deviceid"
                                value={formData.deviceid}
                                onChange={handleChange}
                                placeholder="Enter device ID"
                                disabled={isLoading}
                              />
                              {errors.deviceid && (
                                <div className="invalid-feedback">{errors.deviceid}</div>
                              )}
                            </div>
                          </div>

                          {/* Serial ID */}
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label htmlFor="serialid">
                                Serial ID <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${errors.serialid ? 'is-invalid' : ''}`}
                                id="serialid"
                                name="serialid"
                                value={formData.serialid}
                                onChange={handleChange}
                                placeholder="Enter serial ID"
                                disabled={isLoading}
                              />
                              {errors.serialid && (
                                <div className="invalid-feedback">{errors.serialid}</div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          {/* Active Status */}
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label htmlFor="active">Status</label>
                              <select 
                                className="form-control" 
                                id="active"
                                name="active" 
                                value={formData.active.toString()}
                                onChange={handleChange}
                                disabled={isLoading}
                              >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                              </select>
                            </div>
                          </div>

                          {/* JSON Configuration */}
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label htmlFor="json">
                                JSON Configuration
                                <small className="text-muted ml-2">(Optional)</small>
                              </label>
                              <textarea
                                className={`form-control ${errors.json ? 'is-invalid' : ''}`}
                                id="json"
                                name="json"
                                rows="4"
                                value={formData.json}
                                onChange={handleJsonChange}
                                onBlur={handleJsonBlur}
                                placeholder="Enter valid JSON"
                                disabled={isLoading}
                              />
                              {errors.json && (
                                <div className="invalid-feedback">{errors.json}</div>
                              )}
                              <small className="form-text text-muted">
                                Enter configuration in JSON format (e.g., {`{"key": "value"}`})
                              </small>
                            </div>
                          </div>
                        </div>

                        {/* Form Actions */}
                        <div className="form-footer mt-4">
                          <div className="row">
                            <div className="col-sm-12">
                              <button
                                type="submit"
                                className="btn btn-info mr-2"
                                disabled={isLoading}
                              >
                                {isLoading ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                    Adding...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-save mr-2"></i>
                                    Add Catalogue
                                  </>
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate("/AWSIOTCoreList")}
                                disabled={isLoading}
                              >
                                <i className="fas fa-times mr-2"></i>
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Success Message */}
      {message === "Catalog added successfully!" && (
        <SuccessMessage message={message} />
      )}

      {/* Error Message */}
      {message && message !== "Catalog added successfully!" && (
        <FailedMessage message={message} />
      )}
    </>
  );
};

export default AddAWSIOTCore;